import { useState } from "react";
import { Trash2, Loader2, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { METHODS, fmtDate, todayISO } from "./crmShared";

export const ContactLog = ({ prospect, onChange }) => {
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState("Call");
  const [summary, setSummary] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/crm/prospects/${prospect.id}/contacts`, { date, method, summary, next_follow_up_date: next || null });
      onChange(r.data); setSummary(""); setNext(""); setDate(todayISO()); toast.success("Contact logged");
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Couldn't log contact"); }
    finally { setBusy(false); }
  };
  const remove = async (cid) => {
    try { onChange((await api.delete(`/crm/prospects/${prospect.id}/contacts/${cid}`)).data); }
    catch { toast.error("Couldn't remove entry"); }
  };

  return (
    <div data-testid="contact-log" className="space-y-3">
      <div className="rounded-xl border border-gray-800 bg-black/20 p-3 space-y-2">
        <div className="flex gap-2">
          <Input data-testid="contact-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-black/30 border-gray-800 w-40" />
          <select data-testid="contact-method" value={method} onChange={(e) => setMethod(e.target.value)} className="h-9 bg-black/30 border border-gray-800 rounded-md px-2 text-sm text-white">{METHODS.map((m) => <option key={m}>{m}</option>)}</select>
        </div>
        <Input data-testid="contact-summary" value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="What happened? e.g. Spoke with pastor, wants a demo" className="bg-black/30 border-gray-800" />
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Next follow-up</span>
          <Input data-testid="contact-next" type="date" value={next} onChange={(e) => setNext(e.target.value)} className="bg-black/30 border-gray-800 w-40" />
          <button data-testid="contact-add" onClick={add} disabled={busy || !summary.trim() || !date} className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg gold-gradient text-black text-xs font-semibold hover:brightness-110 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquarePlus className="h-3.5 w-3.5" />} Log contact
          </button>
        </div>
      </div>
      {(prospect.contacts || []).length === 0 ? <p data-testid="contact-log-empty" className="text-xs text-gray-500 text-center py-4">No contacts logged yet.</p> : (
        <ol className="relative border-l border-gray-800 ml-2 space-y-3">
          {prospect.contacts.map((c) => (
            <li key={c.id} data-testid={`contact-entry-${c.id}`} className="ml-4 group">
              <span className="absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full bg-amber-400/80" />
              <div className="flex items-center gap-2 text-[11px] text-gray-500">
                <span className="font-semibold text-gray-300">{fmtDate(c.date)}</span><span className="px-1.5 py-0.5 rounded bg-gray-800 text-gray-400">{c.method}</span>
                <button data-testid={`contact-delete-${c.id}`} onClick={() => remove(c.id)} className="ml-auto opacity-60 group-hover:opacity-100 text-gray-500 hover:text-red-400 transition-opacity" title="Remove"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <p className="text-sm text-gray-200 mt-0.5 whitespace-pre-wrap">{c.summary}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};
